import ReactQuill from 'react-quill';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Radio,
  Select,
  Space,
  Typography
} from 'antd';
import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type {
  IAssessmentKind,
  IAssessmentQuestionKind
} from 'interfaces/professor_area/evaluations';

const QUESTION_KIND_OPTIONS: {
  label: string;
  value: IAssessmentQuestionKind;
}[] = [
  { label: 'Objetiva', value: 'objective' },
  { label: 'Discursiva', value: 'discursive' }
];

const allowedQuestionKinds = (
  assessmentKind: IAssessmentKind | undefined
): IAssessmentQuestionKind[] => {
  if (assessmentKind === 'objective') {
    return ['objective'];
  }

  if (assessmentKind === 'discursive') {
    return ['discursive'];
  }
  return ['objective', 'discursive'];
};

interface ModalCreateEvaluationStepQuestionsProps {
  assessmentKind: IAssessmentKind | undefined;
}

export const ModalCreateEvaluationStepQuestions = ({
  assessmentKind
}: ModalCreateEvaluationStepQuestionsProps) => {
  const questions =
    Form.useWatch<{ kind?: IAssessmentQuestionKind }[]>('questions');

  const questionKinds = allowedQuestionKinds(assessmentKind);
  const defaultQuestionKind: IAssessmentQuestionKind =
    assessmentKind === 'discursive' ? 'discursive' : 'objective';

  return (
    <>
      <Typography.Title level={4} className="!mb-2">
        Adicione as questões
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="!mb-6 text-sm">
        Cadastre as questões da avaliação. Questões objetivas precisam de pelo
        menos duas alternativas e uma marcada como correta.
      </Typography.Paragraph>

      {!assessmentKind && (
        <Alert
          className="!mb-4"
          type="warning"
          showIcon
          message="Volte ao passo anterior e escolha o tipo de questões da avaliação."
        />
      )}

      <Form.List
        name="questions"
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length === 0) {
                throw new Error('Adicione pelo menos uma questão');
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.length === 0 && (
              <Empty description="Nenhuma questão adicionada" />
            )}

            {fields.map((field, index) => {
              const currentKind =
                questions?.[field.name]?.kind ?? defaultQuestionKind;

              return (
                <Card
                  key={field.key}
                  size="small"
                  className="!mb-4"
                  title={`Questão ${index + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    >
                      Remover
                    </Button>
                  }
                >
                  <Form.Item
                    label="Tipo"
                    name={[field.name, 'kind']}
                    initialValue={defaultQuestionKind}
                    rules={[{ required: true, message: 'Selecione o tipo' }]}
                  >
                    <Select
                      options={QUESTION_KIND_OPTIONS.filter(opt =>
                        questionKinds.includes(opt.value)
                      )}
                      disabled={questionKinds.length === 1}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Enunciado"
                    name={[field.name, 'statement']}
                    rules={[
                      {
                        required: true,
                        message: 'Informe o enunciado da questão'
                      }
                    ]}
                  >
                    <ReactQuill theme="snow" />
                  </Form.Item>

                  <Form.Item
                    label="Gabarito / feedback (opcional)"
                    name={[field.name, 'feedback']}
                  >
                    <ReactQuill theme="snow" />
                  </Form.Item>

                  {currentKind === 'objective' && (
                    <AlternativesInput questionName={field.name} />
                  )}
                </Card>
              );
            })}

            <Form.ErrorList errors={errors} />

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({ kind: defaultQuestionKind })}
              disabled={!assessmentKind}
              block
            >
              Adicionar questão
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
};

interface AlternativesInputProps {
  questionName: number;
}

const AlternativesInput = ({ questionName }: AlternativesInputProps) => (
  <Form.Item
    label="Alternativas"
    required
    name={[questionName, 'correct_alternative']}
    rules={[
      {
        validator: async (_, value) => {
          if (value === undefined || value === null) {
            throw new Error('Marque a alternativa correta');
          }
        }
      }
    ]}
  >
    <Radio.Group className="!w-full">
      <Form.List
        name={[questionName, 'alternatives']}
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 2) {
                throw new Error('Adicione pelo menos duas alternativas');
              }
            }
          }
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Flex
                key={field.key}
                align="flex-start"
                gap={8}
                className="!mb-2"
              >
                <Radio value={field.name} style={{ marginTop: 8 }} />
                <Form.Item
                  className="!mb-0 flex-1"
                  name={[field.name, 'statement']}
                  rules={[
                    {
                      required: true,
                      message: 'Informe o texto da alternativa'
                    }
                  ]}
                >
                  <ReactQuill theme="snow" />
                </Form.Item>
                <Button
                  size="small"
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(field.name)}
                  title={`Remover alternativa ${index + 1}`}
                />
              </Flex>
            ))}

            <Form.ErrorList errors={errors} />

            <Space className="!mt-2">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({ statement: '' })}
              >
                Adicionar alternativa
              </Button>
            </Space>
          </>
        )}
      </Form.List>
    </Radio.Group>
  </Form.Item>
);
